import { styled, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Column } from 'components/Layout';
import AvatarWithName from 'components/new/AvatarWithName';
import { useLayoutContext } from 'contexts/LayoutContext';
import { FundingSource } from 'further-types/lp/investment';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import { LpFeeCommitmentStatus } from 'further-types/lp/lp-fee-template';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import NumberInput from 'further-ui/components/NumberInput';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import {
  FundingSourceLabels,
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import { round } from 'lodash';
import { ApprovalSummaryData } from '..';

const UnderlinedTypography = styled(Typography)(() => ({
  textDecorationLine: 'underline',
  textDecorationStyle: 'dotted',
}));

export type GroupedFees = Array<{
  id: string;
  initialTotal: number;
  accrualDate: CalendarDay;
  lpFees: ApprovalSummaryData['fullFeeData'];
}>;

export type GroupedFeesWithDefaultTransactions = {
  totalPages: number;
  totalCount: number;
  feesTotalValue: number;
  defaultTransactions: ApprovalSummaryData['defaultTransactions'];
  groupedFees: GroupedFees;
};

const IndividualFeeTable: React.FC<{
  currencyLcy?: CurrencyCode;
  feeList: GroupedFees[number]['lpFees'];
  feeType: ListLpFeesCategory;
  showBorder?: boolean;
  hideTopBorder?: boolean;
  onFeeChange?: (input: {
    id: string;
    feeExTaxLcy: number;
    salesTaxAmountLcy: number;
    fundingSource: FundingSource;
    feeIncTaxLcy: number;
  }) => void;
}> = ({
  currencyLcy,
  feeList,
  feeType,
  showBorder = false,
  hideTopBorder = false,
  onFeeChange,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const { options: fundingSourceOptions } = useFundingSourceOptions(
    selectedVehicleId ?? '',
    false,
  );
  const isApproved = feeType === ListLpFeesCategory.ApprovedFees;

  const updateRow = ({
    id,
    feeExTaxLcy,
    salesTaxAmountLcy,
    fundingSource,
  }: {
    id: string;
    feeExTaxLcy: number;
    salesTaxAmountLcy: number;
    fundingSource: FundingSource;
  }) => {
    const feeIncTaxLcy = round(feeExTaxLcy + salesTaxAmountLcy, 2);
    onFeeChange?.({
      id,
      feeExTaxLcy,
      salesTaxAmountLcy,
      fundingSource,
      feeIncTaxLcy,
    });
  };

  const mappedFees = feeList.map((fee) => ({ ...fee, id: fee._id }));

  const columns: Array<TableColumn<(typeof mappedFees)[number]>> = [
    {
      key: 'limitedPartner',
      label: 'Limited partner',
      width: 300,
      render: ({ limitedPartner }) => <AvatarWithName name={limitedPartner} />,
    },
    {
      key: 'accrualDate',
      label: 'Accrual date',
      width: 130,
      render: ({ accrualDate }) => accrualDate.toReadable(),
    },
    {
      key: 'lpFeeTemplateName',
      label: 'Fee template',
      width: 300,
      render: ({
        lpFeeTemplateName,
        lpFeeTemplateFeeName,
        insideOutsideCommitment,
        calculationBasis,
      }) => (
        <Tooltip
          title={
            <Column spacing="xxs">
              <Typography variant="caption" color="inherit">
                <b>Fee name:</b> {lpFeeTemplateFeeName}
              </Typography>
              <Typography variant="caption" color="inherit">
                <b>Inside/outside commitment:</b>{' '}
                {LpFeeCommitmentStatusLabels.get(insideOutsideCommitment)}
              </Typography>
              <Typography variant="caption" color="inherit">
                <b>Calculation basis:</b>{' '}
                {LpFeeCalculationBasisLabels.get(calculationBasis)}
              </Typography>
            </Column>
          }
        >
          <UnderlinedTypography variant="inherit">
            {lpFeeTemplateName}
          </UnderlinedTypography>
        </Tooltip>
      ),
    },
    {
      key: 'feeExTaxLcy',
      label: 'Fee',
      width: 200,
      render: ({ id, feeExTaxLcy, salesTaxAmountLcy, fundingSource }) => {
        return isApproved ? (
          numberToCurrencyString(feeExTaxLcy, { currency: currencyLcy })
        ) : (
          <NumberInput
            value={feeExTaxLcy}
            currency={currencyLcy}
            decimals={2}
            fullWidth
            currencyStartAdornment
            onChange={(newValue) => {
              updateRow({
                id,
                feeExTaxLcy: newValue ?? 0,
                salesTaxAmountLcy,
                fundingSource,
              });
            }}
            size="small"
          />
        );
      },
    },
    {
      key: 'salesTaxAmountLcy',
      label: 'Sales tax',
      width: 200,
      render: ({ salesTaxAmountLcy, feeExTaxLcy, fundingSource, id }) => {
        return isApproved ? (
          numberToCurrencyString(salesTaxAmountLcy, { currency: currencyLcy })
        ) : (
          <NumberInput
            size="small"
            value={salesTaxAmountLcy}
            decimals={2}
            fullWidth
            currency={currencyLcy}
            currencyStartAdornment
            onChange={(newValue) => {
              updateRow({
                id,
                feeExTaxLcy,
                salesTaxAmountLcy: newValue ?? 0,
                fundingSource,
              });
            }}
          />
        );
      },
    },
    {
      key: 'feeIncTaxLcy',
      label: 'Total charge',
      width: 200,
      render: ({ feeIncTaxLcy }) =>
        numberToCurrencyString(feeIncTaxLcy, { currency: currencyLcy }),
    },
    {
      key: 'generalCapital',
      label: 'General capital',
      width: 200,
      render: ({ generalCapital }) =>
        numberToCurrencyString(generalCapital ?? 0, { currency: currencyLcy }),
    },
    {
      key: 'fundingSource',
      width: 270,
      label: isApproved ? 'Fees paid by' : 'Pay fees by',
      render: ({
        fundingSource,
        generalCapital,
        id,
        feeExTaxLcy,
        salesTaxAmountLcy,
        lpFeePaymentDate,
        insideOutsideCommitment,
      }) => {
        const label = FundingSourceLabels[fundingSource];
        return isApproved ? (
          fundingSource === FundingSource.CapitalCall ? (
            <Tooltip title={lpFeePaymentDate}>
              <UnderlinedTypography variant="inherit">
                {label}
              </UnderlinedTypography>
            </Tooltip>
          ) : (
            label
          )
        ) : (
          <BasicSelect
            fullWidth
            value={fundingSource}
            options={fundingSourceOptions}
            disabled={
              generalCapital <= 0 ||
              insideOutsideCommitment ===
                LpFeeCommitmentStatus.OutsideCommitment
            }
            onChange={(value) => {
              updateRow({
                id,
                feeExTaxLcy,
                salesTaxAmountLcy,
                fundingSource: value,
              });
            }}
          />
        );
      },
    },
  ];

  return (
    <Table
      borderless={!showBorder}
      scrollable
      showTopBorder={!hideTopBorder}
      rows={mappedFees}
      columns={columns}
    />
  );
};

export default IndividualFeeTable;
